'use client';

import type { ReactNode } from 'react';
import { Badge, Spinner, theme } from '@frameio/vapor';
import {
  type ProjectMountStatus,
  useProjectMountStatus,
} from '../hooks/useMountedProjects';
import TEXT from './generated/TEXT/MountStatusBadge';

/** Mount statuses that show a badge. */
export type VisibleMountStatus = Exclude<
  ProjectMountStatus,
  'unknown' | 'unmounted'
>;

export const isVisibleMountStatus = (
  status: ProjectMountStatus | null
): status is VisibleMountStatus =>
  status === 'mounted' || status === 'mounting' || status === 'ejecting';

/** Human-readable description of a visible mount status. */
export function getMountStatusLabel(status: VisibleMountStatus) {
  switch (status) {
    case 'mounting':
      return TEXT.mountingLabel;
    case 'ejecting':
      return TEXT.ejectingLabel;
    default:
      return TEXT.mountedLabel;
  }
}

type MountStatusBadgeProps = {
  status: ProjectMountStatus | null;
  /** Ring around the badge; match the surface behind the icon. */
  borderColor?: string;
  children: ReactNode;
};

/**
 * Badges `children` with a Drive mount status: a green dot when mounted, a
 * spinner while mounting or ejecting, and nothing otherwise.
 */
export function MountStatusBadge({
  status,
  borderColor = theme.colors.background.panel,
  children,
}: MountStatusBadgeProps) {
  const isBusy = status === 'mounting' || status === 'ejecting';

  return (
    <Badge
      backgroundColor={isBusy ? 'transparent' : 'success'}
      borderColor={borderColor}
      borderSize={1}
      content={
        isBusy ? (
          // Inline SVGs sit on the text baseline; line-height 0 centers it.
          <div style={{ lineHeight: 0 }}>
            <Spinner
              aria-label={getMountStatusLabel(status)}
              delay={0}
              isLoading={true}
              size="xx-small"
            />
          </div>
        ) : undefined
      }
      data-testid={`drive-mount-status-${status}`}
      isShowing={isVisibleMountStatus(status)}
      label=""
      placement="top-right"
      size={isBusy ? 12 : 6}
    >
      {children}
    </Badge>
  );
}

type DriveMountStatusBadgeProps = Omit<MountStatusBadgeProps, 'status'> & {
  projectId: string;
};

/** {@link MountStatusBadge} for a project's current Drive mount status. */
export function DriveMountStatusBadge({
  projectId,
  ...props
}: DriveMountStatusBadgeProps) {
  const status = useProjectMountStatus(projectId);
  return <MountStatusBadge status={status} {...props} />;
}

TEXT.DEFINE({
  /** Describes a project mounted by the Frame.io Drive app */
  mountedLabel: 'Project is Mounted',
  /** Describes a project the Frame.io Drive app is mounting */
  mountingLabel: 'Project is Mounting',
  /** Describes a project the Frame.io Drive app is ejecting */
  ejectingLabel: 'Project is Ejecting',
});
